import React, { useEffect } from 'react';
import Grid from '@material-ui/core/Grid';
import { Skeleton } from '@material-ui/lab';
import useOnScreen from 'hooks/useOnScreen';
import moment from 'moment';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import strings from 'strings';
import EmailItemCard from 'UI/components/atoms/EmailItemCard';
import { When } from 'UI/components/atoms/When';
import { BulkMenuItems } from 'UI/constants/defaults';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { getRemainingTimeToDate } from 'UI/utils';

import { useStyles } from './styles';

const SentSkelleton = ({ classes }) => (
  <Grid container className={classes.skelleton}>
    <Grid container justify="space-between">
      <Skeleton variant="text" width="50%" />
      <Skeleton variant="text" width="15%" />
    </Grid>
    <Grid container justify="space-between">
      <Skeleton variant="text" width="60%" />
      <Skeleton variant="circle" width={20} height={20} />
    </Grid>
    <Skeleton variant="text" width="75%" />
  </Grid>
);

const { emptyStates: emptyStatesStrings } = strings.bulkEmails;

export const dateFormatters = {
  default: date => {
    const strFormat = 'YYYYMMDD';
    const now = moment().format(strFormat);
    const yesterday = moment().subtract(1, 'days').format(strFormat);
    const sentDate = moment(date);
    let format = '';

    if (now === sentDate.format(strFormat)) {
      format = sentDate.format('hh:mm a');
    } else if (yesterday === sentDate.format(strFormat)) {
      format = 'Yesterday';
    } else {
      format = sentDate.format('MM/DD/YYYY');
    }
    return format;
  },
  daysRemaining: date => {
    const remainingTime = getRemainingTimeToDate(date);
    return remainingTime ? `${remainingTime?.quantity} ${remainingTime?.unitOfTime} remaining` : '';
  }
};

const bulkModeDateFormatterMap = {
  [BulkMenuItems.Sent]: dateFormatters.default,
  [BulkMenuItems.Drafts]: dateFormatters.default,
  [BulkMenuItems.Schedule]: dateFormatters.daysRemaining
};

/**
 * @param {Object} props
 * @param {number} [props.emailInPreviewId]
 * @param {Array} [props.emailsList]
 * @param {boolean} [props.isLastEmailLoaded]
 * @param {boolean} [props.loading]
 * @param {Function} props.onLoadMoreEmails
 * @param {Function} props.onSelectEmail
 * @param {string} props.selectedMenuItem
 */
const BulkEmailsList = ({
  emailInPreviewId = null,
  emailsList = [],
  isLastEmailLoaded = true,
  loading = false,
  onLoadMoreEmails,
  onSelectEmail,
  selectedMenuItem
}) => {
  const classes = useStyles();
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const [loaderRef, isLoaderVisible] = useOnScreen();

  const handleSelectEmail = selectedMailId => onSelectEmail && onSelectEmail(selectedMailId);

  useEffect(() => {
    if (isLoaderVisible && !loading && !isLastEmailLoaded && onLoadMoreEmails) {
      onLoadMoreEmails();
    }
  }, [isLoaderVisible, loading, isLastEmailLoaded, onLoadMoreEmails]);

  return (
    <Grid className={classes.root}>
      {emailsList.map(email => (
        /**
         * TODO: remove EmailItemCard component and handle directly on this component
         * EmailItemCard component is not useful anymore and everything can be
         * handled perfectly within EmailsList component
         */
        <EmailItemCard
          key={email.key}
          bulkId={email?.id}
          formattedDate={bulkModeDateFormatterMap[selectedMenuItem](
            email?.send_date || email?.created_at
          )}
          /* eslint-disable-next-line no-underscore-dangle */
          hasAttachments={email.emailBody?.__meta__?.attachments_count > 0}
          isActiveCard={emailInPreviewId === email.id}
          onSelectEmail={handleSelectEmail}
          previewText={email.emailBody?.text}
          searchProjectName={
            email.searchProject?.name || emptyStatesStrings.emailItems.searchProjectName
          }
          sequence={email?.bulkSequence}
          shouldShowAILabel={
            checkIfFeatureFlagEnabled(FeatureFlags.BulkEmailListItemCardAILabel) &&
            !!email?.prompt_id
          }
          subject={email.emailBody?.subject || emptyStatesStrings.emailItems.subject}
          warning={email?.warning}
        />
      ))}
      <When condition={!isLastEmailLoaded && !loading}>
        <div data-testid="emails-loader-sensor" ref={loaderRef}>
          <SentSkelleton classes={{ skelleton: classes.skelleton }} />
        </div>
      </When>
    </Grid>
  );
};

export default BulkEmailsList;
